import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Loader2, Save, RefreshCw, Target } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RULE_FIELDS = [
  { key: 'company_name', label: 'Company Name Present' },
  { key: 'phone', label: 'Phone Present' },
  { key: 'email', label: 'Email Present' },
  { key: 'website', label: 'Website Present' },
  { key: 'address', label: 'Address Present' },
  { key: 'city_state_zip', label: 'City + State + ZIP Present' },
  { key: 'sic_code', label: 'SIC Code Present' },
  { key: 'naics_code', label: 'NAICS Code Present' },
  { key: 'employee_size', label: 'Employee Size Present' },
  { key: 'company_revenue', label: 'Company Revenue Present' },
];

const defaultSettings = {
  enabled: true,
  max_score: 100,
  rules: RULE_FIELDS.reduce((acc, item) => ({ ...acc, [item.key]: 0 }), {}),
  verification_penalties: {
    invalid_phone: 40,
    invalid_domain: 35,
  },
};

export const LeadScoringSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('dme_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/lead-scoring/settings`, {
        headers: getHeaders(),
      });
      setSettings({
        enabled: res.data?.enabled ?? true,
        max_score: res.data?.max_score ?? 100,
        rules: {
          ...defaultSettings.rules,
          ...(res.data?.rules || {}),
        },
        verification_penalties: {
          ...defaultSettings.verification_penalties,
          ...(res.data?.verification_penalties || {}),
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load lead scoring settings');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const totalWeights = useMemo(
    () => Object.values(settings.rules || {}).reduce((sum, value) => sum + Number(value || 0), 0),
    [settings.rules]
  );

  const updateRule = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      rules: {
        ...prev.rules,
        [key]: Number(value || 0),
      },
    }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const payload = {
        enabled: settings.enabled,
        max_score: Number(settings.max_score || 100),
        rules: settings.rules,
        verification_penalties: settings.verification_penalties,
      };
      await axios.put(`${API_URL}/api/admin/lead-scoring/settings`, payload, {
        headers: getHeaders(),
      });
      toast.success('Lead scoring settings saved');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save lead scoring settings');
    } finally {
      setSaving(false);
    }
  };

  const rescoreAll = async () => {
    try {
      setRescoring(true);
      const res = await axios.post(
        `${API_URL}/api/admin/lead-scoring/rescore-all`,
        {},
        { headers: getHeaders() }
      );

      if (res.data?.job?.id) {
        const jobId = res.data.job.id;
        toast.success(`Rescore queued (job ${jobId.slice(0, 8)}...)`);

        let finished = false;
        for (let attempt = 0; attempt < 20; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          const jobRes = await axios.get(`${API_URL}/api/admin/lead-scoring/rescore-jobs/${jobId}`, {
            headers: getHeaders(),
          });
          const status = jobRes.data?.job?.status;
          if (status === 'completed') {
            const totalUpdated = jobRes.data?.job?.result?.total_updated || 0;
            toast.success(`Rescore complete: ${totalUpdated} leads updated`);
            finished = true;
            break;
          }
          if (status === 'failed') {
            toast.error(jobRes.data?.job?.error || 'Rescore job failed');
            finished = true;
            break;
          }
        }

        if (!finished) {
          toast.success('Rescore is still running in background. You can continue using the app.');
        }
      } else {
        toast.success(`Rescore complete: ${res.data?.total_updated || 0} leads updated`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rescore platform leads');
    } finally {
      setRescoring(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center" data-testid="lead-scoring-settings-loading">
        <Loader2 className="w-7 h-7 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-scoring-settings-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" data-testid="lead-scoring-settings-title">
            <Target className="w-5 h-5" />
            Global Lead Scoring
          </CardTitle>
          <CardDescription data-testid="lead-scoring-settings-description">
            Configure scoring rules applied across business, seller, and website leads.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3" data-testid="lead-scoring-settings-summary">
            <Badge variant={settings.enabled ? 'default' : 'secondary'} data-testid="lead-scoring-enabled-badge">
              {settings.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <Badge variant="outline" data-testid="lead-scoring-total-weight-badge">
              Total Rule Weight: {totalWeights.toFixed(2)}
            </Badge>
            <Badge variant="outline" data-testid="lead-scoring-max-score-badge">
              Max Score: {settings.max_score}
            </Badge>
            <Badge variant="destructive" data-testid="lead-scoring-penalty-phone-badge">
              Invalid Phone Penalty: -{settings.verification_penalties?.invalid_phone ?? 40}
            </Badge>
            <Badge variant="destructive" data-testid="lead-scoring-penalty-domain-badge">
              Invalid Domain Penalty: -{settings.verification_penalties?.invalid_domain ?? 35}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="flex items-center gap-2" data-testid="lead-scoring-enabled-row">
              <Checkbox
                data-testid="lead-scoring-enabled-checkbox"
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, enabled: checked === true }))}
              />
              <Label>Enable Lead Scoring</Label>
            </div>

            <div>
              <Label htmlFor="lead-scoring-max-score">Max Score</Label>
              <Input
                id="lead-scoring-max-score"
                type="number"
                data-testid="lead-scoring-max-score-input"
                value={settings.max_score}
                min={1}
                max={1000}
                onChange={(e) => setSettings((prev) => ({ ...prev, max_score: Number(e.target.value || 100) }))}
              />
            </div>

            <div>
              <Label htmlFor="lead-scoring-phone-penalty">Invalid Phone Penalty</Label>
              <Input
                id="lead-scoring-phone-penalty"
                type="number"
                min={0}
                data-testid="lead-scoring-phone-penalty-input"
                value={settings.verification_penalties?.invalid_phone ?? 40}
                onChange={(e) => setSettings((prev) => ({
                  ...prev,
                  verification_penalties: {
                    ...(prev.verification_penalties || {}),
                    invalid_phone: Number(e.target.value || 0),
                  },
                }))}
              />
            </div>

            <div>
              <Label htmlFor="lead-scoring-domain-penalty">Invalid Domain Penalty</Label>
              <Input
                id="lead-scoring-domain-penalty"
                type="number"
                min={0}
                data-testid="lead-scoring-domain-penalty-input"
                value={settings.verification_penalties?.invalid_domain ?? 35}
                onChange={(e) => setSettings((prev) => ({
                  ...prev,
                  verification_penalties: {
                    ...(prev.verification_penalties || {}),
                    invalid_domain: Number(e.target.value || 0),
                  },
                }))}
              />
            </div>

            <div className="flex items-end justify-end gap-2">
              <Button
                variant="outline"
                data-testid="lead-scoring-refresh-button"
                onClick={fetchSettings}
                disabled={saving || rescoring}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                data-testid="lead-scoring-save-button"
                onClick={saveSettings}
                disabled={saving || rescoring}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Rules
              </Button>
            </div>
          </div>

          <div className="space-y-3" data-testid="lead-scoring-rules-list">
            {RULE_FIELDS.map((rule) => (
              <div
                key={rule.key}
                className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center rounded border p-3"
                data-testid={`lead-scoring-rule-row-${rule.key}`}
              >
                <div className="md:col-span-2">
                  <p className="text-sm font-medium" data-testid={`lead-scoring-rule-label-${rule.key}`}>{rule.label}</p>
                  <p className="text-xs text-muted-foreground">Points added when this field is present.</p>
                </div>
                <Input
                  type="number"
                  min={0}
                  step="0.5"
                  data-testid={`lead-scoring-rule-input-${rule.key}`}
                  value={settings.rules?.[rule.key] ?? 0}
                  onChange={(e) => updateRule(rule.key, e.target.value)}
                />
              </div>
            ))}
          </div>

          <div className="pt-2">
            <Button
              variant="secondary"
              data-testid="lead-scoring-rescore-all-button"
              onClick={rescoreAll}
              disabled={saving || rescoring}
            >
              {rescoring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Rescore All Existing Leads
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
